import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Maximize, 
  Minimize, 
  Users, 
  TrendingUp,
  TrendingDown,
  Clock,
  Zap,
  Activity,
  RefreshCw,
  CheckCircle2,
  CalendarClock,
  Scan,
  ArrowUpRight,
  Minus
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.07 } }
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } }
};

const tooltipStyle = {
  backgroundColor: '#1c1c1f',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '12px',
  color: '#ececef',
  boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
  padding: '10px 14px',
  fontSize: '13px'
};

export default function LiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(new Date());
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data: res } = await axios.get(`${API}/api/dashboard/live`, { headers });
      setData(res);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching live data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const getDelta = () => {
    if (!data) return { value: 0, positive: true };
    const diff = data.checkins_today - data.checkins_yesterday;
    return { value: Math.abs(diff), positive: diff >= 0 };
  };

  const delta = getDelta();

  const getCurrentHour = () => new Date().getHours().toString().padStart(2, '0');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
          className="w-10 h-10 border-2 border-indigo-500/30 border-t-indigo-400 rounded-full"
        />
      </div>
    );
  }

  return (
    <div data-testid="live-dashboard" className="min-h-screen bg-[#09090b] flex flex-col relative overflow-hidden">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-indigo-500/[0.04] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[300px] bg-emerald-500/[0.03] rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="relative z-20 px-6 py-4 flex items-center justify-between border-b border-white/[0.04]">
        <div className="flex items-center gap-4">
          <Link 
            to="/dashboard" 
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-[#6e6e73] hover:text-white hover:bg-white/5 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Dashboard</span>
          </Link>
          <div className="w-px h-6 bg-white/[0.06]" />
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/40" />
              <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping opacity-40" />
            </div>
            <span className="text-sm font-semibold text-emerald-400 tracking-wide">EN VIVO</span>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <button
            onClick={fetchData}
            data-testid="refresh-button"
            className="p-2 rounded-xl text-[#6e6e73] hover:text-white hover:bg-white/5 transition-all"
            title="Actualizar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={toggleFullscreen}
            data-testid="live-fullscreen-toggle"
            className="p-2 rounded-xl text-[#6e6e73] hover:text-white hover:bg-white/5 transition-all"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
          <div className="text-right">
            <p className="text-2xl font-bold text-white tabular-nums tracking-tight">
              {currentTime.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
            <p className="text-[11px] text-[#48484d] mt-0.5">
              {currentTime.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <div className="w-px h-8 bg-white/[0.06]" />
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <span className="text-white font-bold text-xs">N</span>
            </div>
            <span className="text-sm font-medium text-white">{user?.business_name || 'Nova'}</span>
          </div>
        </div>
      </header>

      {/* Main grid */}
      <main className="flex-1 p-6 relative z-10 overflow-auto">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-[1600px] mx-auto space-y-6"
        >
          {/* Hero stat cards */}
          <motion.div variants={container} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Check-ins Today - Hero */}
            <motion.div 
              variants={item} 
              className="col-span-2 lg:col-span-1 relative overflow-hidden rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-indigo-500/10 via-indigo-600/5 to-transparent p-6"
              style={{ boxShadow: '0 0 40px rgba(99, 102, 241, 0.06), inset 0 1px 0 rgba(255,255,255,0.04)' }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-indigo-500/15 flex items-center justify-center">
                  <Scan className="w-5 h-5 text-indigo-400" />
                </div>
                <div className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                  delta.positive 
                    ? 'bg-emerald-500/10 text-emerald-400' 
                    : 'bg-red-500/10 text-red-400'
                }`}>
                  {delta.positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {delta.value === 0 ? <Minus className="w-3 h-3" /> : null}
                  {delta.value > 0 ? `${delta.positive ? '+' : '-'}${delta.value}` : 'Igual'}
                </div>
              </div>
              <p className="text-5xl font-extrabold text-white tabular-nums tracking-tight">
                {data?.checkins_today || 0}
              </p>
              <p className="text-sm text-[#6e6e73] mt-1.5 font-medium">Entradas hoy</p>
              <p className="text-xs text-[#48484d] mt-0.5">Ayer: {data?.checkins_yesterday || 0}</p>
            </motion.div>

            {/* Active Members */}
            <motion.div 
              variants={item}
              className="relative overflow-hidden rounded-2xl border border-emerald-500/15 bg-gradient-to-br from-emerald-500/8 to-transparent p-6"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <p className="text-3xl font-extrabold text-white tabular-nums">{data?.active_members || 0}</p>
              <p className="text-sm text-[#6e6e73] mt-1 font-medium">Miembros activos</p>
              <p className="text-xs text-[#48484d] mt-0.5">de {data?.total_clients || 0} totales</p>
            </motion.div>

            {/* Peak Hour */}
            <motion.div 
              variants={item}
              className="relative overflow-hidden rounded-2xl border border-amber-500/15 bg-gradient-to-br from-amber-500/8 to-transparent p-6"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}
            >
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center mb-4">
                <Zap className="w-5 h-5 text-amber-400" />
              </div>
              <p className="text-3xl font-extrabold text-white tabular-nums">{data?.peak_hour || '—'}</p>
              <p className="text-sm text-[#6e6e73] mt-1 font-medium">Hora pico</p>
              <p className="text-xs text-[#48484d] mt-0.5">{data?.peak_count || 0} entradas</p>
            </motion.div>

            {/* Appointments Today */}
            <motion.div 
              variants={item}
              className="relative overflow-hidden rounded-2xl border border-purple-500/15 bg-gradient-to-br from-purple-500/8 to-transparent p-6"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}
            >
              <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center mb-4">
                <CalendarClock className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-3xl font-extrabold text-white tabular-nums">{data?.todays_appointments?.length || 0}</p>
              <p className="text-sm text-[#6e6e73] mt-1 font-medium">Citas hoy</p>
              <p className="text-xs text-[#48484d] mt-0.5">Programadas</p>
            </motion.div>
          </motion.div>

          {/* Bottom section: Chart + Live Feed + Appointments */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Hourly Chart - takes 2 cols */}
            <motion.div 
              variants={item} 
              className="lg:col-span-2 rounded-2xl border border-white/[0.06] bg-[#0f0f11] p-6"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)' }}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-base font-semibold text-white">Afluencia por hora</h3>
                  <p className="text-xs text-[#48484d] mt-0.5">Entradas registradas hoy</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <Activity className="w-3.5 h-3.5 text-indigo-400" />
                  <span className="text-xs text-[#a1a1a6] font-medium">Tiempo real</span>
                </div>
              </div>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.hourly_chart || []} barCategoryGap="20%">
                    <XAxis 
                      dataKey="hour" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: '#48484d', fontSize: 11, fontWeight: 500 }}
                      dy={8}
                      interval={1}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#48484d', fontSize: 11 }}
                      dx={-8}
                      allowDecimals={false}
                    />
                    <Tooltip 
                      contentStyle={tooltipStyle}
                      formatter={(value) => [`${value} entradas`, '']}
                      labelFormatter={(label) => `${label}:00`}
                      cursor={{ fill: 'rgba(99,102,241,0.06)', radius: 6 }}
                    />
                    <Bar dataKey="checkins" radius={[5, 5, 2, 2]} maxBarSize={32}>
                      {(data?.hourly_chart || []).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.hour === getCurrentHour() ? '#6366f1' : 'rgba(99,102,241,0.25)'}
                          stroke={entry.hour === getCurrentHour() ? '#818cf8' : 'transparent'}
                          strokeWidth={1}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Live Feed */}
            <motion.div 
              variants={item}
              className="rounded-2xl border border-white/[0.06] bg-[#0f0f11] flex flex-col"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)' }}
            >
              <div className="p-5 pb-3 border-b border-white/[0.04]">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-white">Entradas recientes</h3>
                  <div className="relative">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <div className="absolute inset-0 w-2 h-2 rounded-full bg-emerald-400 animate-ping opacity-30" />
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto max-h-[340px] p-3 space-y-1">
                <AnimatePresence>
                  {(data?.recent_checkins || []).length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 text-center">
                      <Clock className="w-8 h-8 text-[#2c2c2e] mb-3" />
                      <p className="text-sm text-[#48484d]">Sin entradas hoy</p>
                      <p className="text-xs text-[#2c2c2e] mt-1">Las entradas aparecerán aquí</p>
                    </div>
                  ) : (
                    (data?.recent_checkins || []).map((checkin, i) => {
                      const time = new Date(checkin.timestamp);
                      return (
                        <motion.div
                          key={`${checkin.client_id}-${checkin.timestamp}`}
                          initial={{ opacity: 0, x: 12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.04, duration: 0.35 }}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.02] transition-colors group"
                        >
                          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{checkin.client_name}</p>
                            <p className="text-[11px] text-[#48484d]">
                              {time.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <Link 
                            to="/checkin"
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-white/5"
                          >
                            <ArrowUpRight className="w-3.5 h-3.5 text-[#6e6e73]" />
                          </Link>
                        </motion.div>
                      );
                    })
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>

          {/* Appointments row */}
          {(data?.todays_appointments?.length || 0) > 0 && (
            <motion.div 
              variants={item}
              className="rounded-2xl border border-white/[0.06] bg-[#0f0f11] p-6"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)' }}
            >
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-semibold text-white">Agenda del día</h3>
                  <p className="text-xs text-[#48484d] mt-0.5">{data.todays_appointments.length} citas programadas</p>
                </div>
                <Link 
                  to="/agenda" 
                  className="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors flex items-center gap-1"
                >
                  Ver agenda
                  <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                {data.todays_appointments.map((apt, i) => {
                  const isPast = apt.time < currentTime.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.05 }}
                      className={`shrink-0 w-44 p-4 rounded-xl border transition-all ${
                        isPast 
                          ? 'border-white/[0.04] bg-white/[0.01] opacity-50' 
                          : 'border-indigo-500/15 bg-indigo-500/[0.04] hover:border-indigo-500/25'
                      }`}
                    >
                      <p className={`text-lg font-bold tabular-nums ${isPast ? 'text-[#6e6e73]' : 'text-white'}`}>
                        {apt.time}
                      </p>
                      <p className={`text-sm mt-1 truncate ${isPast ? 'text-[#48484d]' : 'text-[#a1a1a6]'}`}>
                        {apt.client_name}
                      </p>
                      {isPast && (
                        <span className="text-[10px] text-[#48484d] uppercase tracking-wider font-semibold mt-2 block">Completada</span>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-3 border-t border-white/[0.04] flex items-center justify-between">
        <p className="text-[11px] text-[#2c2c2e]">
          Actualización automática cada 30s
        </p>
        <p className="text-[11px] text-[#2c2c2e]">
          Última: {lastRefresh.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </p>
      </footer>
    </div>
  );
}
